import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Clock, ArrowRight, Bell, GitBranch, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import PowerBIReports from '@/components/PowerBIReports';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchActivities();
    fetchNotifications();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activity?limit=10`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      await axios.put(`${API}/notifications/${notif.id}/read`);
      setNotifications(notifications.map(n => n.id === notif.id ? {...n, is_read: true} : n));
      if (notif.resource_type === 'document' && notif.resource_id) {
        navigate(`/documents/${notif.resource_id}`);
      }
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-'; const date = new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-1">
            Dashboard
          </h1>
          <p className="text-gray-500">Welcome back, {user.full_name}</p>
        </div>
        {/* Quick Actions - moved from bottom to top-right, permission-gated */}
        <div className="flex flex-wrap gap-2 justify-end" data-testid="quick-actions">
          {(user?.permissions?.can_view_all_documents || user?.is_super_admin || (user?.role || '').toLowerCase() === 'admin') && (
            <Link to="/documents">
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-xl h-10 px-4" data-testid="quick-action-documents">
                <FileText size={16} className="mr-2" weight="bold" />
                Browse Documents
              </Button>
            </Link>
          )}
          {(user?.permissions?.can_approve_reviews || user?.is_super_admin || (user?.role || '').toLowerCase() === 'admin') && (
            <Link to="/workflows">
              <Button variant="outline" className="rounded-xl h-10 px-4 border-gray-200 text-gray-700 hover:bg-gray-100" data-testid="quick-action-workflows">
                <GitBranch size={16} className="mr-2" />
                Workflows
              </Button>
            </Link>
          )}
          {(user?.permissions?.can_view_transmittals || user?.is_super_admin || (user?.role || '').toLowerCase() === 'admin') && (
            <Link to="/transmittals">
              <Button variant="outline" className="rounded-xl h-10 px-4 border-gray-200 text-gray-700 hover:bg-gray-100" data-testid="quick-action-transmittals">
                <PaperPlaneTilt size={16} className="mr-2" />
                Transmittals
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Analytics & Reports Section */}
      <div className="mb-8">
        <PowerBIReports />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <div className="bg-white rounded-2xl shadow-apple-sm border border-gray-100 overflow-hidden" data-testid="recent-documents">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Recent Documents</h3>
            <Link to="/documents" className="text-sm text-primary hover:text-primary-600 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {stats?.recent_documents && stats.recent_documents.length > 0 ? (
              stats.recent_documents.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/documents/${doc.id}`}
                  className="flex items-center justify-between px-6 py-3 hover:bg-gray-50 transition-colors"
                  data-testid={`recent-doc-${doc.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText size={18} weight="regular" className="text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{doc.name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(doc.created_at ? (String(doc.created_at).endsWith('Z') || String(doc.created_at).includes('+') ? doc.created_at : doc.created_at + 'Z') : 0).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">
                    {(doc.file_size / 1024).toFixed(1)} KB
                  </span>
                </Link>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">No documents yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl shadow-apple-sm border border-gray-100 overflow-hidden" data-testid="recent-activity">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Recent Activity</h3>
            <Link to="/activity" className="text-sm text-primary hover:text-primary-600 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {activities.length > 0 ? (
              activities.slice(0, 6).map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 px-6 py-3" data-testid={`activity-${activity.id}`}>
                  <div className="w-2 h-2 mt-2 bg-primary rounded-full flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-900">{activity.user_name}</span>
                      {' '}{activity.action}{' '}
                      <span className="font-medium text-gray-900">{activity.resource_name}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDate(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <Clock size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* My Notifications Section */}
      <div className="mt-6 bg-white rounded-2xl shadow-apple-sm border border-gray-100 overflow-hidden" data-testid="my-notifications">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell size={18} weight="fill" className="text-primary" />
            <h3 className="text-base font-semibold text-gray-900">My Notifications</h3>
            {notifications.filter(n => !n.is_read).length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {notifications.filter(n => !n.is_read).length} new
              </span>
            )}
          </div>
        </div>
        <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.slice(0, 10).map((notif) => (
              <div 
                key={notif.id} 
                className={`flex items-start gap-3 px-6 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                onClick={() => handleNotificationClick(notif)}
                data-testid={`notification-${notif.id}`}
              >
                <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${!notif.is_read ? 'bg-blue-500' : 'bg-gray-300'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{notif.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(notif.created_at)}
                  </p>
                  {notif.resource_type === 'document' && notif.resource_id && (
                    <p className="text-xs text-blue-600 mt-1 hover:underline">Click to view document →</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <Bell size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">No notifications</p>
            </div>
          )}
        </div>
      </div>
        </div>
  );
};

export default Dashboard;
